/**
 * 비상벨 호출 시뮬레이터 — 맵의 비상벨을 (a) 목록에서 고르거나 (b) 맵에서 직접
 * 클릭해 하드웨어 벨 입력을 모사한다(triggerBellCall → lights sos-push).
 * 벨→CCTV 해석(emergency_bells)과 연결 CCTV 점멸은 조명제어서버가 실제 코드
 * 경로로 수행하며, 결과는 기존 dry-run 캡처 + statusStore 맵 하이라이트 +
 * fan-out 이벤트 패널로 시각화한다 — 어떤 CCTV 에 점멸(blink) 명령이 나갔는지
 * 이벤트 패널에서 검증한다. CCTV 직접 호출 섹션은 기존 triggerEmergency 유지.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  DeleteOutline as ClearIcon,
  NotificationsActive as BellIcon,
  PlaylistPlay as AutoRunIcon,
} from '@mui/icons-material';
import { LightsSimulatorService } from '../../services/LightsSimulatorService';
import { useLightsCaptureSession } from './hooks/useLightsCaptureSession';
import { useSimInteractionStore } from './model/simInteractionStore';
import { useStatusStore } from '../../map-editor-features/status/model/statusStore';
import type { MapCctv, CctvLightLink } from './mapAssetFix';
import type { SimulatorCctv, SimulatorEmergencyBell } from '../../models/Simulator';
import { sleep, waitForLightEvent, type AutoCheckResult, type SimAutoRunHandle } from './autoTestUtils';
import AutoTestReportDialog from './AutoTestReportDialog';

interface EmergencyBellSimulatorProps {
  /** 현재 맵의 비상벨 목록 */
  bells: SimulatorEmergencyBell[];
  /** 시뮬레이터 CCTV 목록 API 결과 — 이름+IP 보강 */
  cctvs: SimulatorCctv[];
  /** 맵 graphJson 에서 추출한 실제 배치 CCTV 이름 */
  mapCctvNames?: string[];
  /** 맵 CCTV 이름+IP — "이름 (IP)" 다중 선택지 우선 소스 */
  mapCctvList?: MapCctv[];
  /** CCTV↔조명 관계 — 호출 시 연결 조명 셀 점멸에 사용 */
  cctvLightLinks?: CctvLightLink[];
  /** 현재 선택된 맵 id (향후 확장용) */
  mapId: number | null;
}

const EmergencyBellSimulator = forwardRef<SimAutoRunHandle, EmergencyBellSimulatorProps>(({
  bells,
  cctvs,
  mapCctvNames = [],
  mapCctvList = [],
  cctvLightLinks = [],
}, ref) => {
  // ── dry-run 캡처 세션 (조명 시뮬레이터와 동일 훅) ──────────────
  const {
    dryRunEnabled,
    togglingMode,
    modeError,
    toggleMode,
    events,
    clearEvents,
  } = useLightsCaptureSession();

  // 비상 호출 중 CCTV 표시(맵 bloom 점멸). alert 시 추가, release 시 제거.
  const setEmergencyCctv = useStatusStore(s => s.setEmergencyCctv);
  // 호출 CCTV 에 연결된 조명 셀 점멸 등록/해제.
  const setEmergencyLights = useStatusStore(s => s.setEmergencyLights);

  // CCTV 이름 → 연결 조명 셀 id (저장 관계 우선, 없으면 최근접 파티셔닝 — mapAssetFix).
  const lightIdsByCctv = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const link of cctvLightLinks) m.set(link.cctvName, link.lightIds);
    return m;
  }, [cctvLightLinks]);

  // CCTV 점멸 + 연결 조명 점멸을 함께 켜고 끈다.
  const markEmergency = useCallback(
    (cctvName: string, on: boolean) => {
      setEmergencyCctv(cctvName, on);
      setEmergencyLights(cctvName, lightIdsByCctv.get(cctvName) ?? [], on);
    },
    [setEmergencyCctv, setEmergencyLights, lightIdsByCctv],
  );

  const [triggerError, setTriggerError] = useState<string | null>(null);
  // "연결된 CCTV 없음" 등 호출은 됐지만 효과가 없을 수 있는 경우를 알리는 경고.
  const [triggerWarn, setTriggerWarn] = useState<string | null>(null);
  // 마지막으로 호출된 CCTV 이름 — "호출된 CCTV: <name>" 표시용.
  const [lastCalledCctv, setLastCalledCctv] = useState<string | null>(null);
  // 현재 호출(alert) 상태인 벨 id 집합 — 버튼 1개 토글(호출↔해제)의 근거.
  const [activeBellIds, setActiveBellIds] = useState<Set<string>>(new Set());
  // 맵 포커스 — 선택/호출한 벨·CCTV 를 맵에서 하이라이트 + 센터링.
  const setMapFocus = useSimInteractionStore(s => s.setMapFocus);
  const mapFocus = useSimInteractionStore(s => s.mapFocus);

  // CCTV 선택지(이름) + 이름→IP 매핑. 맵 CCTV(mapCctvList: 이름+IP)를 우선 소스로,
  // 시뮬레이터 API(cctvs: ip_address)와 맵 이름만 목록(mapCctvNames)을 보조로 합친다.
  const cctvNameOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const add = (n: string) => {
      if (n && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    };
    mapCctvList.forEach(c => add(c.name));
    cctvs.forEach(c => add(c.name));
    mapCctvNames.forEach(add);
    return out;
  }, [mapCctvList, cctvs, mapCctvNames]);

  const ipByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of mapCctvList) if (c.name && c.ip) m.set(c.name, c.ip);
    for (const c of cctvs) if (c.name && c.ip_address && !m.has(c.name)) m.set(c.name, c.ip_address);
    return m;
  }, [mapCctvList, cctvs]);

  // "이름 (IP)" 라벨. IP 가 없으면 이름만.
  const optionLabel = useCallback(
    (name: string) => {
      const ip = ipByName.get(name);
      return ip ? `${name} (${ip})` : name;
    },
    [ipByName],
  );

  // 다중 선택된 CCTV 이름.
  const [selectedCctvs, setSelectedCctvs] = useState<string[]>([]);

  // 맵 클릭 트리거 구독.
  const pendingBellTrigger = useSimInteractionStore(s => s.pendingBellTrigger);
  const consumePending = useSimInteractionStore(s => s.consumePending);
  // 맵에서 CCTV 클릭 → 다중 선택에 추가(이미 있으면 유지).
  const selectedCctv = useSimInteractionStore(s => s.selectedCctv);
  useEffect(() => {
    const name = selectedCctv?.name;
    if (!name) return;
    setSelectedCctvs(prev => (prev.includes(name) ? prev : [...prev, name]));
    // nonce 로 같은 CCTV 재클릭도 반영. setSelectedCctvs 는 안정적이라 의존성 제외.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCctv?.nonce]);

  const handleToggleMode = () => {
    void toggleMode();
  };

  // 벨 단위 비상 호출 — 하드웨어 벨 입력을 SOS 경로(sos-push)로 모사한다.
  // 벨→CCTV 해석은 조명제어서버(lights)가 수행하므로, 실제 점멸 대상 CCTV 는
  // fan-out 이벤트 패널의 blink 명령으로 확인한다(벨 신호 집계로 약 1초 지연).
  // 성공 시 activeBellIds 를 갱신해 버튼이 호출↔해제로 토글되고, 맵 포커스를
  // 게시해 어떤 벨이 호출됐는지 맵에서 하이라이트 + 센터링된다.
  const fireBell = useCallback(
    async (bell: SimulatorEmergencyBell, action: 'alert' | 'release') => {
      setTriggerError(null);
      setTriggerWarn(null);
      const cctvName = bell.linked_cctv_id;
      if (!cctvName) {
        setTriggerWarn('연결된 CCTV 가 없어 호출할 수 없습니다.');
        return;
      }
      setMapFocus({ kind: 'bell', id: bell.id });
      try {
        await LightsSimulatorService.triggerBellCall(cctvName, action, bell.linked_custom_id);
        markEmergency(cctvName, action === 'alert');
        setLastCalledCctv(cctvName);
        setActiveBellIds(prev => {
          const next = new Set(prev);
          if (action === 'alert') next.add(bell.id);
          else next.delete(bell.id);
          return next;
        });
      } catch (e) {
        setTriggerError(e instanceof Error ? e.message : '비상 호출 실패');
      }
    },
    [markEmergency, setMapFocus],
  );

  // 버튼 1개 토글 — 호출 중이면 해제, 아니면 호출.
  const toggleBell = useCallback(
    (bell: SimulatorEmergencyBell) => {
      void fireBell(bell, activeBellIds.has(bell.id) ? 'release' : 'alert');
    },
    [fireBell, activeBellIds],
  );

  // 여러 CCTV 에 동시에 비상 호출/해제.
  const fireEmergencyMany = useCallback(
    async (names: string[], action: 'alert' | 'release') => {
      setTriggerError(null);
      setTriggerWarn(null);
      const targets = names.filter(Boolean);
      if (targets.length === 0) {
        setTriggerWarn('선택된 CCTV 가 없습니다.');
        return;
      }
      try {
        await Promise.all(targets.map(n => LightsSimulatorService.triggerEmergency(n, action)));
        targets.forEach(n => markEmergency(n, action === 'alert'));
        setLastCalledCctv(targets.join(', '));
      } catch (e) {
        setTriggerError(e instanceof Error ? e.message : '비상 호출 실패');
      }
    },
    [markEmergency],
  );

  // ── 자동 실행: 비상벨을 하나씩 맵에서 보여주며 호출하고, 연결된 CCTV 의 조명이
  // 실제로 점멸(캡처 이벤트 on=true)하는지, 연결된 조명 자체가 없는 벨이 있는지 확인한다. ──
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoReport, setAutoReport] = useState<AutoCheckResult[] | null>(null);
  const [showReport, setShowReport] = useState(false);
  const stopRequestedRef = useRef(false);

  // 판정 루프만 순수 추출(로컬 report/showReport state는 안 건드림) — 개별
  // "자동 실행" 버튼과 통합 자동 테스트(SimulatorPage index.tsx)가 공유한다.
  const runCheckLoop = useCallback(async (): Promise<AutoCheckResult[]> => {
    if (!dryRunEnabled) await toggleMode();
    const seek = await LightsSimulatorService.getEvents(0);
    let cursor = seek.cursor;
    const results: AutoCheckResult[] = [];
    for (const bell of bells) {
        if (stopRequestedRef.current) break;
        setMapFocus({ kind: 'bell', id: bell.id });
        await sleep(600);

        const cctvName = bell.linked_cctv_id;
        if (!cctvName) {
          results.push({
            name: bell.name || bell.id,
            passed: false,
            detail: '연결된 CCTV 가 없어 호출할 수 없습니다.',
          });
          setAutoReport([...results]);
          continue;
        }

        const lightIds = lightIdsByCctv.get(cctvName) ?? [];
        const hasLightLink = lightIds.length > 0;
        let blinkOk = false;
        let errorDetail = '';
        try {
          await LightsSimulatorService.triggerBellCall(cctvName, 'alert', bell.linked_custom_id);
          markEmergency(cctvName, true);
          const w = await waitForLightEvent(cursor, ev => ev.cctvName === cctvName && ev.on);
          cursor = w.cursor;
          blinkOk = !!w.hit;
          await LightsSimulatorService.triggerBellCall(cctvName, 'release', bell.linked_custom_id);
          markEmergency(cctvName, false);
        } catch (e) {
          errorDetail = e instanceof Error ? e.message : '비상 호출 실패';
        }

        const passed = blinkOk && hasLightLink && !errorDetail;
        const detail =
          errorDetail ||
          `${blinkOk ? '점멸 이벤트 캡처됨' : '점멸 이벤트 캡처 안 됨'} · ${
            hasLightLink ? `연결 조명 ${lightIds.length}개` : '연결된 조명 없음'
          }`;
        results.push({ name: bell.name || bell.id, passed, detail });
        setAutoReport([...results]);
    }
    return results;
  }, [bells, dryRunEnabled, toggleMode, lightIdsByCctv, markEmergency, setMapFocus]);

  const handleAutoRun = useCallback(async () => {
    if (bells.length === 0 || autoRunning) return;
    setAutoRunning(true);
    setAutoReport(null);
    stopRequestedRef.current = false;
    try {
      await runCheckLoop();
      setShowReport(true);
    } finally {
      setAutoRunning(false);
    }
  }, [bells.length, autoRunning, runCheckLoop]);

  const handleStopAutoRun = useCallback(() => {
    stopRequestedRef.current = true;
  }, []);

  useImperativeHandle(ref, () => ({
    runAutoCheck: async () => {
      if (autoRunning) return [];
      setAutoRunning(true);
      setAutoReport(null);
      stopRequestedRef.current = false;
      try {
        const results = await runCheckLoop();
        setAutoReport(results);
        return results;
      } finally {
        setAutoRunning(false);
      }
    },
    stopAutoCheck: handleStopAutoRun,
  }), [runCheckLoop, autoRunning, handleStopAutoRun]);

  // 맵 클릭 → 비상벨 id(폴백 name)로 벨을 찾아 토글(호출 중이면 해제).
  useEffect(() => {
    if (!pendingBellTrigger) return;
    if (!dryRunEnabled) {
      // dry-run 이 꺼져 있으면 무시하고 요청만 비운다(잘못된 점등 방지).
      consumePending();
      return;
    }
    const { bellId } = pendingBellTrigger;
    const bell =
      bells.find(b => b.id === bellId) ?? bells.find(b => b.name === bellId);
    if (bell) {
      toggleBell(bell);
    }
    consumePending();
  }, [pendingBellTrigger, dryRunEnabled, bells, toggleBell, consumePending]);

  const hasBells = bells.length > 0;

  return (
    <Stack spacing={2}>
      {/* ── 1. dry-run 토글 (조명 시뮬레이터와 동일 스타일) ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            Dry-run 캡처 모드
          </Typography>
          <Chip
            size="small"
            label={dryRunEnabled ? '활성' : '비활성'}
            color={dryRunEnabled ? 'success' : 'default'}
            sx={{ flexShrink: 0 }}
          />
          <Button
            variant={dryRunEnabled ? 'outlined' : 'contained'}
            color={dryRunEnabled ? 'error' : 'primary'}
            size="small"
            startIcon={dryRunEnabled ? <StopIcon /> : <PlayIcon />}
            onClick={handleToggleMode}
            disabled={togglingMode}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            data-testid="bell-drymode-toggle"
          >
            {togglingMode ? '전환 중...' : dryRunEnabled ? 'OFF' : 'ON'}
          </Button>
        </Stack>
        {modeError && (
          <Alert
            severity={modeError.includes('허용') || modeError.includes('403') ? 'warning' : 'error'}
            sx={{ mt: 1 }}
          >
            {modeError}
          </Alert>
        )}
        {!dryRunEnabled && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            ON 으로 전환하면 비상벨 호출의 fan-out 결과를 캡처해 맵에 표시합니다.
            맵 위 비상벨 마커 클릭도 ON 일 때만 동작합니다. 하드웨어로는 전송되지 않습니다.
          </Typography>
        )}
      </Paper>

      {/* ── 2. 비상벨 목록 / 수동 폴백 ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2">
            비상벨 목록{hasBells ? ` (${bells.length})` : ''}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {lastCalledCctv && (
              <Typography variant="caption" color="text.secondary" data-testid="bell-last-called">
                호출된 CCTV: <strong>{lastCalledCctv}</strong>
              </Typography>
            )}
            {autoRunning ? (
              <Button size="small" variant="outlined" color="error" onClick={handleStopAutoRun}>
                정지
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoRunIcon />}
                onClick={handleAutoRun}
                disabled={!hasBells}
              >
                자동 실행
              </Button>
            )}
          </Stack>
        </Stack>

        {triggerError && <Alert severity="error" sx={{ mb: 1 }}>{triggerError}</Alert>}
        {triggerWarn && <Alert severity="warning" sx={{ mb: 1 }}>{triggerWarn}</Alert>}

        {hasBells ? (
          <Stack spacing={1} data-testid="bell-list">
            {bells.map(bell => {
              const linked = bell.linked_cctv_id;
              const hasLink = !!linked;
              const rowDisabled = !dryRunEnabled || !hasLink;
              const active = activeBellIds.has(bell.id);
              const focused = mapFocus?.kind === 'bell' && mapFocus.id === bell.id;
              return (
                <Box
                  key={bell.id}
                  onClick={() => setMapFocus({ kind: 'bell', id: bell.id })}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    rowGap: 1,
                    py: 0.5,
                    px: 0.75,
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: focused ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: focused ? 'action.selected' : 'action.hover' },
                  }}
                  data-testid={`bell-row-${bell.id}`}
                >
                  <BellIcon
                    fontSize="small"
                    color={active ? 'error' : hasLink ? 'warning' : 'disabled'}
                  />
                  <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    {/* Chip(div) 이 <p> 안에 못 들어가므로 컨테이너는 div 로 렌더. */}
                    <Typography component="div" variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {bell.name || bell.id}
                      {active && (
                        <Chip size="small" color="error" label="호출 중" sx={{ ml: 0.75, height: 18 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {hasLink ? `→ ${linked}` : '연결된 CCTV 없음'}
                    </Typography>
                  </Box>
                  {/* 버튼 1개 토글 — 호출하면 같은 버튼이 해제로 바뀐다. */}
                  <Button
                    variant={active ? 'outlined' : 'contained'}
                    color={active ? 'error' : 'warning'}
                    size="small"
                    disabled={rowDisabled}
                    onClick={e => {
                      e.stopPropagation(); // 행 클릭(포커스만)과 분리
                      toggleBell(bell);
                    }}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    data-testid={`bell-call-${bell.id}`}
                  >
                    {active ? '해제' : '호출'}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Alert severity="info" sx={{ py: 0.5 }} data-testid="bell-empty-fallback">
            이 맵에는 등록된 비상벨이 없습니다. 아래에서 CCTV 를 직접 선택해 비상 호출을
            테스트할 수 있습니다.
          </Alert>
        )}
      </Paper>

      {/* ── CCTV 직접 비상 호출 (여러 개 선택, 이름(IP) 표시) ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          CCTV 직접 비상 호출 (다중 선택)
        </Typography>
        {/* 이름(IP) 로 여러 CCTV 선택. 맵 위 CCTV 클릭 시 자동 추가된다. 선택/입력은
            dry-run 과 무관, 실제 호출(아래 버튼)만 dry-run ON 을 요구한다. */}
        <Autocomplete
          multiple
          size="small"
          fullWidth
          options={cctvNameOptions}
          value={selectedCctvs}
          onChange={(_, v) => {
            // 새로 추가된 CCTV 는 맵에서 위치를 바로 보여준다(하이라이트+센터링).
            const added = v.find(n => !selectedCctvs.includes(n));
            setSelectedCctvs(v);
            if (added) setMapFocus({ kind: 'cctv', name: added });
          }}
          getOptionLabel={optionLabel}
          renderTags={(value, getTagProps) =>
            value.map((name, index) => (
              <Chip
                {...getTagProps({ index })}
                key={name}
                size="small"
                label={optionLabel(name)}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              label="CCTV (이름 · IP)"
              placeholder="여러 개 선택"
              helperText={
                cctvNameOptions.length > 0
                  ? '이름(IP)로 여러 CCTV 선택 · 맵 위 CCTV 클릭 시 자동 추가'
                  : '맵/lights 에 CCTV 가 없습니다'
              }
            />
          )}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant="contained"
            color="warning"
            size="small"
            disabled={!dryRunEnabled || selectedCctvs.length === 0}
            onClick={() => fireEmergencyMany(selectedCctvs, 'alert')}
            sx={{ flex: '1 1 auto', whiteSpace: 'nowrap' }}
            data-testid="bell-manual-call"
          >
            호출{selectedCctvs.length > 1 ? ` (${selectedCctvs.length})` : ''}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!dryRunEnabled || selectedCctvs.length === 0}
            onClick={() => fireEmergencyMany(selectedCctvs, 'release')}
            sx={{ flex: '1 1 auto', whiteSpace: 'nowrap' }}
            data-testid="bell-manual-release"
          >
            해제
          </Button>
        </Stack>
      </Paper>

      {/* ── 3. 라이브 fan-out 패널 (조명 시뮬레이터와 동일) ── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            Fan-out 이벤트
            {dryRunEnabled && (
              <Chip size="small" label="폴링 중" color="success" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<ClearIcon />}
            onClick={clearEvents}
            disabled={events.length === 0}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            초기화
          </Button>
        </Stack>
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {dryRunEnabled
              ? '비상벨을 호출하면 캡처된 이벤트가 여기 표시됩니다.'
              : 'Dry-run 을 ON 하고 비상벨을 호출하세요.'}
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ maxHeight: 260, overflow: 'auto' }}>
            {events.map(ev => (
              <Box
                key={ev.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  py: 0.25,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ width: 72, flexShrink: 0 }}>
                  {ev.at}
                </Typography>
                <Chip
                  size="small"
                  label={ev.cctvName}
                  color="primary"
                  variant="outlined"
                  sx={{ maxWidth: 160 }}
                />
                <Chip
                  size="small"
                  label={ev.lightType}
                  color="default"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={ev.on ? `${ev.value}%` : 'OFF'}
                  color={ev.on ? 'warning' : 'default'}
                />
                <Typography variant="caption" color="text.secondary">
                  {ev.mode}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Divider />
      <Typography variant="caption" color="text.secondary">
        목록의 [호출] 버튼 또는 맵 위 비상벨 마커를 클릭하면 하드웨어 벨 입력을 모사한
        신호가 조명제어서버로 전달되고, 서버가 벨과 연결된 CCTV 를 찾아 점멸(blink)
        명령을 보냅니다. 실제 점멸 대상은 Fan-out 이벤트에서 확인하세요(약 1초 지연).
        "자동 실행"은 목록의 비상벨을 하나씩 맵에서 보여주며 호출해 점멸 여부와
        연결된 조명 유무를 리포트로 보여줍니다.
      </Typography>

      <AutoTestReportDialog
        open={showReport}
        title="비상벨 호출 — 자동 실행 리포트"
        results={autoReport ?? []}
        onClose={() => setShowReport(false)}
      />
    </Stack>
  );
});

EmergencyBellSimulator.displayName = 'EmergencyBellSimulator';

export default EmergencyBellSimulator;
